import * as React from 'react';
import classNames from 'styles/utils/classNames';
import { PayoffTableProps, PayoffTableCell } from './types';
import { profitPercentUnavailableMessage } from 'utils/shared/quantlib/returnOnCost';

const PayoffTable: React.FC<PayoffTableProps> = ({ payoffTable }) => {
  if (!payoffTable || payoffTable.length === 0) {
    return null;
  }

  return (
    <div className="mt-6 overflow-x-auto">
      <table className="min-w-full">
        <thead>
          <tr>
            <th className="rounded-tl-md bg-palette-black-3 p-2 text-xs font-medium text-white">
              Profit $
            </th>
            {payoffTable[0].map((entry: PayoffTableCell, index: number) => (
              <th
                key={index}
                className={classNames(
                  'bg-palette-black-4 p-2 text-xs font-medium uppercase text-white',
                  index === payoffTable[0].length - 1 && 'rounded-tr-md',
                )}
              >
                {new Date(entry.timestamp).toLocaleDateString('en-US', {
                  month: 'numeric',
                  day: 'numeric',
                })}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {payoffTable.map((row: PayoffTableCell[], rowIndex: number) => {
            return (
              <tr key={rowIndex} className={classNames('bg-palette-black-3 text-xs text-white')}>
                <td
                  className={classNames(
                    'flex w-16 p-1',
                    rowIndex === payoffTable.length - 1 && 'rounded-bl-md',
                  )}
                >
                  <span className="flex-1">${row[0].stockPrice}</span>
                  <span className="px-2 text-[11px] text-gray-500">
                    {row[0].stockPriceRelativeToSpot}
                  </span>
                </td>
                {row.map((cell: PayoffTableCell, cellIndex: number) => {
                  const profitPercent = cell.profitPercent;
                  const isProfit = profitPercent != null && profitPercent >= 0;
                  const color = isProfit ? '0, 188, 55' : '168, 0, 35';
                  const maxPercentValue = 100;
                  const opacity = Math.max(
                    0.05,
                    Math.min(1, Math.abs(profitPercent ?? 0) / maxPercentValue),
                  );
                  const cellStyle = {
                    backgroundColor:
                      profitPercent != null && Number.isFinite(profitPercent)
                        ? `rgba(${color}, ${opacity})`
                        : 'rgba(0, 0, 0, 1)',
                  };

                  const value = new Intl.NumberFormat('en-US').format(Math.round(cell.profit));

                  return (
                    <td
                      key={cellIndex}
                      style={cellStyle}
                      title={
                        profitPercent == null
                          ? profitPercentUnavailableMessage(cell.profitPercentReason)
                          : undefined
                      }
                      className={classNames(
                        'p-1 text-center',
                        rowIndex === payoffTable.length - 1 &&
                          cellIndex === row.length - 1 &&
                          'rounded-br-md',
                      )}
                    >
                      {value}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default PayoffTable;
